import { findIndex, pick } from 'lodash';
import {
	useDeleteScenarioMutation,
	useUpdateScenarioMutation,
	useCreateScenarioMutation,
	usePromoteScenarioAsBaselineMutation
} from 'graph/dist/generated';
import { getFieldFunctions } from 'utils/graphql';
import { ScenarioModel } from 'model/ScenarioModel';
import { useAnalyzeSituation } from 'services/situationAnalysisService';

export interface CreateScenarioResult {
	scenarioId: string;
	needsElaboration: boolean;
	simulationQuery?: string;
}

export const useScenarioController = () => {
	const [updateScenario] = useUpdateScenarioMutation();
	const [deleteScenario] = useDeleteScenarioMutation();
	const [createScenario] = useCreateScenarioMutation();
	const [promoteScenarioMutation] = usePromoteScenarioAsBaselineMutation();

	const { elaborateScenario } = useAnalyzeSituation();

	const handleCreateScenario = async (
		projectId: string,
		_context: any,
		referenceScenario: any,
		values: any,
		callback: Function
	): Promise<CreateScenarioResult | null> => {
		const result = await createScenario({
			variables: {
				projectId,
				name: values.name,
				description: values.description || '',
				impacts: values.impacts || '',
				referenceScenarioId: referenceScenario?.id || null
			}
		});

		const newScenarioId = result.data?.createScenario;

		const needsElaboration = !!(
			values.generateSimulation &&
			values.simulationQuery &&
			newScenarioId
		);

		if (callback) callback(newScenarioId);

		return {
			scenarioId: newScenarioId!,
			needsElaboration,
			simulationQuery: values.simulationQuery
		};
	};

	/**
	 * Lance l'élaboration IA d'un scénario
	 * Doit être appelé après que le modal de progression soit affiché
	 *
	 * @param projectId - L'ID du projet
	 * @param scenarioId - L'ID du scénario
	 * @param query - L'hypothèse du scénario
	 * @param language - La langue de réponse (ex: 'fr', 'en')
	 */
	const launchScenarioElaboration = async (
		projectId: string,
		scenarioId: string,
		query: string,
		language?: string
	) => {
		console.log(
			'🎯 Lancement de la simulation automatique pour le scénario:',
			scenarioId,
			'Langue:',
			language
		);

		try {
			await elaborateScenario({
				projectId,
				query,
				scenarioId,
				language
			});
		} catch (error) {
			console.error(
				'❌ Erreur lors du lancement de la simulation:',
				error
			);
			throw error;
		}
	};

	const handleUpdateScenario = async (
		id: string,
		scenarios: any[],
		values: Object
	) => {
		let index = findIndex(scenarios, (s: any) => s.id === id);

		let newScenario = {
			...pick(scenarios[index], ['description', 'name', 'impacts']),
			...pick(values, ['description', 'name', 'impacts'])
		};

		const result = await updateScenario({
			variables: {
				id: id,
				...newScenario
			},
			update(cache) {
				cache.modify({
					id: `Scenario:${id}`,
					fields: getFieldFunctions(newScenario)
				});
			},
			optimisticResponse: {
				updateScenario: true
			}
		});
	};

	const handlePromoteScenarioAsBaseline = async (
		project,
		scenario: ScenarioModel,
		keepBaselineAsScenario: boolean,
		baselineNewName: string,
		keepScenario: boolean
	) => {
		if (!scenario) return;

		await promoteScenarioMutation({
			variables: {
				projectId: project.id,
				scenarioId: scenario.id,
				keepBaselineAsScenario,
				baselineNewName: baselineNewName || '',
				keepScenario
			}
		});
	};

	const handleDelete = async (projectId: string, id: string | undefined) => {
		if (id == null) {
			return;
		}

		await deleteScenario({
			variables: { id },
			update(cache) {
				cache.modify({
					id: `Project:${projectId}`,
					fields: {
						scenarios(existingScenariosRef, { readField }) {
							return existingScenariosRef.filter(
								(ref: string) => id !== readField('id', ref)
							);
						}
					}
				});
			},
			optimisticResponse: {
				deleteScenario: true
			}
		});
	};

	return {
		createScenario: handleCreateScenario,
		updateScenario: handleUpdateScenario,
		deleteScenario: handleDelete,
		promoteScenarioAsBaseline: handlePromoteScenarioAsBaseline,
		launchScenarioElaboration
	};
};
